import { ref, nextTick } from 'vue';
import { modalUtil } from '@functions/modalUtil';
import axios from 'axios';
import { Capacitor } from '@capacitor/core';
import { App } from '@capacitor/app';
import { CameraPreview } from '@capgo/camera-preview';
import { WebViewLayout } from '@/plugins/webViewLayout';
import {
  captureAndDecode,
  isNativeDecodeAvailable,
  setNativeLicense,
} from '@/plugins/barcodeScanFrame';
import url from '@/config/urls';
import deviceService from '@/services/deviceService.js';

const SCAN_SOUND_FOUND_URL = url.static.scanSound1;
const SCAN_SOUND_NOT_FOUND_URL = url.static.scanSound2;
const SCAN_SOUND_FALLBACK_MS = 2000;

let scanFoundAudio = null;
let scanNotFoundAudio = null;
let isSdkVersionReported = false;

const getOrCreateAudio = (currentAudio, soundUrl) => {
  if (!currentAudio) {
    const audio = new Audio(soundUrl);
    audio.preload = 'auto';
    return audio;
  }
  return currentAudio;
};

const getScanFoundAudio = () => {
  scanFoundAudio = getOrCreateAudio(scanFoundAudio, SCAN_SOUND_FOUND_URL);
  return scanFoundAudio;
};

const getScanNotFoundAudio = () => {
  scanNotFoundAudio = getOrCreateAudio(scanNotFoundAudio, SCAN_SOUND_NOT_FOUND_URL);
  return scanNotFoundAudio;
};

/**
 * バーコードスキャン共通処理
 * 
 * @param {object} options
 * @param {import {'vue'}.Ref<string>} options.loginUrl
 * 
 */
export function useBarcodeScanner({loginUrl, licenseApiUrl, onScanResult}) {
  const { showModal } = modalUtil();

  const cameraAreaRef           = ref(null);
  const videoRef                = ref(null);
  const canvasRef               = ref(null);
  const scanError               = ref('');
  const isCameraPreviewActive   = ref(false);
  const isDecoding              = ref(false);
  const isLicenseReady          = ref(false);
  const isReading               = ref(false);
  const isScanSuccessHighlight  = ref(false);
  const errorMessages           = ref([]);

  const cameraPreviewBodyClass = 'scan-camera-preview-active';
  let cameraPreviewStarted              = false;
  let isContinuousScanning              = false;
  let rotationWatcherCleanup            = null;
  let rotationPluginListenerHandle      = null;
  let fixedCameraPreviewStartOptions    = null;
  let restoreBoundsTimers               = [];
  let isRestoringPreviewBounds          = false;
  const RESTORE_BOUNDS_DELAYS_ON_ORIENTATION_MS = [0];
  const INITIAL_PREVIEW_ZOOM_LEVEL = 1.2;
  const SCAN_INTERVAL_MS = 150;
  const SCAN_IDLE_TIMEOUT_MS = 10000;
  const SCAN_TIMING_LOG = true;

  let scanLoopTimer = null;
  let scanIdleTimeoutTimer = null;
  let scanTimingSeq = 0;
  let lastScanLoopEndedAt = 0;
  let appStateListenerHandles = [];
  let isPausedByAppBackground = false;
  let isScanServiceDisposed = false;
  let nativeDecodeAvailableForSession = isNativeDecodeAvailable();
  let appStateChangeQueue = Promise.resolve();

  const roundMs = (value) => Math.round(value);

  const toTimingMs = (value) => {
    if (value == null || value === '') return null;
    const n = Number(value);
    return Number.isFinite(n) ? roundMs(n) : null;
  };

  const formatTimingMs = (value) => (value == null ? '-' : String(value));

  const logScanTiming = (payload) => {
    if (!SCAN_TIMING_LOG) return;
    console.info(
      '[SCAN:TIMING]'
      + ` n=${payload.n}`
      + ` captureMs=${formatTimingMs(payload.captureMs)}`
      + ` processMs=${formatTimingMs(payload.processMs)}`
      + ` bridgeMs=${formatTimingMs(payload.bridgeMs)}`
      + ` decodeMs=${formatTimingMs(payload.decodeMs)}`
      + ` waitMs=${formatTimingMs(payload.waitMs)}`
      + ` totalMs=${formatTimingMs(payload.totalMs)}`
      + ` found=${payload.found}`
      + ` width=${formatTimingMs(payload.width)}`
      + ` height=${formatTimingMs(payload.height)}`
      + ` source=${payload.source ?? '-'}`
      + ` fallbackReason=${payload.fallbackReason ?? '-'}`
      + ` stage=${payload.stage}`,
    );
  };

  const handleDecodedResult = async (codemark, codedata) => {
    stopContinuousScan();
    await onScanResult(codemark, codedata, {
      playFoundSound: playScanFoundSound,
      playNotFoundSound: playScanNotFoundSound,
    });
  };

  const cancelScanSchedule = () => {
    if (scanLoopTimer !== null) {
      clearTimeout(scanLoopTimer);
      scanLoopTimer = null;
    }
  };

  const cancelScanIdleTimeout = () => {
    if (scanIdleTimeoutTimer !== null) {
      clearTimeout(scanIdleTimeoutTimer);
      scanIdleTimeoutTimer = null;
    }
  };

  const startScanIdleTimeout = () => {
    cancelScanIdleTimeout();
    scanIdleTimeoutTimer = setTimeout(() => {
      scanIdleTimeoutTimer = null;
      if (!isContinuousScanning) return;
      stopContinuousScan();
    }, SCAN_IDLE_TIMEOUT_MS);
  };

  const scheduleNextScan = (delayMs = SCAN_INTERVAL_MS) => {
    if (!isContinuousScanning) return;
    cancelScanSchedule();
    scanLoopTimer = setTimeout(() => {
      scanLoopTimer = null;
      runScanIteration();
    }, delayMs);
  };

  const prepareScanSuccessSound = () => {
    try {
      getScanFoundAudio().load();
      getScanNotFoundAudio().load();
    } catch {
      // 失敗してもスキャンは継続
    }
  };

  const playScanSound = (getAudio) => new Promise((resolve) => {
    let settled = false;
    let fallbackTimer = null;

    const finish = () => {
      if (settled) return;
      settled = true;
      if (fallbackTimer !== null) {
        clearTimeout(fallbackTimer);
      }
      isScanSuccessHighlight.value = false;
      resolve();
    };

    isScanSuccessHighlight.value = true;

    const audio = getAudio();
    audio.currentTime = 0;

    audio.addEventListener('ended', finish, { once: true });
    audio.addEventListener('error', finish, { once: true });

    const setFallbackTimer = () => {
      if (fallbackTimer !== null) {
        clearTimeout(fallbackTimer);
      }
      const durationMs = Number.isFinite(audio.duration) && audio.duration > 0
        ? Math.ceil(audio.duration * 1000) + 100
        : SCAN_SOUND_FALLBACK_MS;
      fallbackTimer = setTimeout(finish, durationMs);
    };

    if (audio.readyState >= 1) {
      setFallbackTimer();
    } else {
      audio.addEventListener('loadedmetadata', setFallbackTimer, { once: true });
      fallbackTimer = setTimeout(finish, SCAN_SOUND_FALLBACK_MS);
    }

    audio.play().catch(() => finish());
  });

  const playScanFoundSound = () => playScanSound(getScanFoundAudio);
  const playScanNotFoundSound = () => playScanSound(getScanNotFoundAudio);

  /**
   * 原因切り分け用の環境・メモリ情報（取れない項目は n/a）。
   */
  const summarizeUserAgent = (ua) => {
    if (!ua) return 'n/a';
    return ua.length > 120 ? `${ua.slice(0, 120)}...` : ua;
  };

  const getMemoryDiagnostics = () => {
    try {
      const mem = performance?.memory;
      if (mem) {
        return `jsHeapUsed=${mem.usedJSHeapSize} jsHeapTotal=${mem.totalJSHeapSize} jsHeapLimit=${mem.jsHeapSizeLimit}`;
      }
    } catch {
      // ignore
    }
    return 'jsHeap=n/a';
  };

  const getEnvironmentDiagnostics = () => {
    const platform = Capacitor.getPlatform?.() ?? 'unknown';
    const ua = typeof navigator !== 'undefined' ? navigator.userAgent : '';
    const screenW = window.screen?.width ?? 'n/a';
    const screenH = window.screen?.height ?? 'n/a';
    const innerW = window.innerWidth ?? 'n/a';
    const innerH = window.innerHeight ?? 'n/a';
    const dpr = window.devicePixelRatio ?? 'n/a';
    const deviceMemory = navigator.deviceMemory ?? 'n/a';
    return [
      `platform=${platform}`,
      `screen=${screenW}x${screenH}`,
      `inner=${innerW}x${innerH}`,
      `dpr=${dpr}`,
      `deviceMemory=${deviceMemory}`,
      getMemoryDiagnostics(),
      `ua=${summarizeUserAgent(ua)}`,
    ].join('\n');
  };

  const appendEnvironmentDiagnostics = (detail = '') => {
    const env = getEnvironmentDiagnostics();
    return detail ? `${detail}\n${env}` : env;
  };

  /**
   * バーコードスキャン機能のクライアント側の失敗をバックエンドに報告する。
   * 送信は待たず、失敗しても画面表示・スキャン継続を妨げない。
   * @param {string} stage 失敗が発生した処理段階
   * @param {string} [detail]
   */
  const postScanLog = (apiUrl, stage, detail) => {
    const fullDetail = appendEnvironmentDiagnostics(detail);
    deviceService.getDeviceUUID()
      .then((deviceUuid) => axios.post(apiUrl, {
        stage,
        detail: fullDetail,
        deviceUuid,
      }))
      .catch(() => {
        // 報告に失敗しても何もしない
      });
  };

  const reportScanError = (stage, detail = '') => {
    postScanLog(url.api.barcodeScan.errorReport, stage, detail);
  };

  /**
   * バーコードスキャン機能の通常ログ（エラーではない情報）をバックエンドに報告する。
   * @param {string} stage ログの種別
   * @param {string} [detail]
   */
  const reportScanInfo = (stage, detail = '') => {
    postScanLog(url.api.barcodeScan.log, stage, detail);
  };

  const reportSdkVersionOnce = async (sdkVersion) => {
    if (isSdkVersionReported) return;
    isSdkVersionReported = true;
    let appVersion = 'n/a';
    try {
      const info = await App.getInfo();
      appVersion = info?.version ?? 'n/a';
    } catch {
      // アプリバージョンが取れなくてもSDKバージョンは報告する
    }
    reportScanInfo('SDK:VERSION', `sdkVersion=${sdkVersion ?? 'n/a'}\nappVersion=${appVersion}`);
  };

  /**
   * カメラ起動・撮影系の失敗時の画面表示（インライン文言）。
   * 状況が回復すれば scanError はクリアされる想定。
   * @param {string} message 画面に表示する文言
   * @param {string} stage 失敗が発生した処理段階
   * @param {string} [detail]
   */
  const setScanError = (message, stage, detail = '') => {
    scanError.value = message;
    reportScanError(stage, detail);
  };

  /**
   * ライセンス取得失敗時の画面表示。
   * 原因切り分けはバックエンドのログで行うため、画面には汎用メッセージのみ表示する。
   */
  const handleLicenseError = () => {
    errorMessages.value = ['エラーが発生しました。アプリを再起動してください。'];
    showModal('apiErrorModal');
  };

  const isLikelyOutOfMemoryError = (error) => {
    if (!error) return false;
    const msg = String(error.message || error || '');
    const name = String(error.name || '');
    if (/OOM|out of memory|Array buffer allocation failed|ENOMEM/i.test(msg)) return true;
    if (name === 'RangeError' && /Invalid typed array length|Array buffer|allocation|out of bounds/i.test(msg)) {
      return true;
    }
    return false;
  };

  const describeThrownError = (error) => {
    if (!error) return 'unknown error';
    const parts = [
      `${error.name || 'Error'}: ${error.message || String(error)}`,
    ];
    if (isLikelyOutOfMemoryError(error)) {
      parts.unshift('likelyOOM=true');
    }
    const status = error.response?.status;
    if (status != null) {
      parts.push(`status=${status}`);
    }
    const apiErr = error.response?.data?.err;
    if (apiErr) {
      parts.push(`code=${apiErr.code ?? ''}`);
      parts.push(`message=${apiErr.message ?? ''}`);
    }
    const messageStatus = error.response?.data?.message?.status;
    if (messageStatus != null) {
      parts.push(`message.status=${messageStatus}`);
    }
    if (error.stack) {
      // モーダル用にスタックを短くする
      const stackLines = String(error.stack).split('\n').slice(0, 6);
      parts.push(...stackLines);
    }
    return parts.join('\n');
  };

  const getDecodeCanvasSize = () => {
    const canvasEl = canvasRef.value;
    return {
      width: canvasEl?.width ?? 1920,
      height: canvasEl?.height ?? 1080,
    };
  };

  /**
   * バーコード解析失敗時の画面表示（エラーモーダル）。
   * 原因切り分けはバックエンドのログで行うため、画面には汎用メッセージのみ表示する。
   * @param {string} stage try 内の失敗段階
   * @param {string} [detail]
   */
  const handleDecodeError = (stage, detail = '') => {
    reportScanError(`DECODE:${stage}`, detail);
    errorMessages.value = ['バーコードの解析中にエラーが発生しました。'];
    stopContinuousScan();
    showModal('apiErrorModal');
  };

  /**
   * バックグラウンド遷移でカメラセッションが閉じたあとのキャプチャ失敗。
   * iOS: captureSessionIsMissing / Android: Camera is closed など。
   * 復帰直後の致命エラー扱いを避ける。
   */
  const isCameraSessionUnavailableError = (error) => {
    const text = [
      getCameraPreviewErrorMessage(error),
      error?.code,
      error?.errorMessage,
      describeThrownError(error),
    ].filter(Boolean).join('\n');
    return /captureSessionIsMissing|session is missing|no capture session|camera is closed|camera is not running|camera not ready|camerapreview not available|not bound to a valid camera/i.test(text);
  };

  /**
   * スキャンループ全体（フレーム取得など）の失敗表示。
   * @param {string} stage
   * @param {string} [detail]
   */
  const handleScanLoopError = (stage, detail = '') => {
    if (!isContinuousScanning || isPausedByAppBackground || isScanServiceDisposed) {
      return;
    }
    reportScanError(`SCAN:${stage}`, detail);
    errorMessages.value = ['読み取り処理に失敗しました。'];
    stopContinuousScan();
    showModal('apiErrorModal');
  };

  // ライセンス取得
  const fetchLicense = async() => {
    try {
      if (!nativeDecodeAvailableForSession) {
        reportScanError('LICENSE:NATIVE_UNAVAILABLE');
        handleLicenseError();
        return false;
      }

      const deviceUuid = await deviceService.getDeviceUUID();
      const res = await axios.post(licenseApiUrl.value ?? licenseApiUrl, { deviceUuid });
      const data = res?.data || {};

      if (data.err) {
        reportScanError('LICENSE:API_ERR', `code=${data.err.code ?? ''}\nmessage=${data.err.message ?? ''}`);
        handleLicenseError();
        return false;
      }

      const licenseBody = data?.message?.body;
      if (!licenseBody) {
        reportScanError('LICENSE:EMPTY_BODY', `message.status=${data?.message?.status ?? 'n/a'}`);
        handleLicenseError();
        return false;
      }

      // ネイティブのバーコード読み取りエンジンにライセンスを設定する（Android / iOS）
      const result = await setNativeLicense(licenseBody);
      if (!result?.success) {
        reportScanError('LICENSE:SET_NATIVE_LICENSE_FAIL', `ret=${result?.ret}`);
        handleLicenseError();
        return false;
      }
      isLicenseReady.value = true;
      reportSdkVersionOnce(result.sdkVersion).catch(() => {
        // SDKバージョンの報告失敗はスキャンに影響させない
      });
      return true;

    } catch (error) {
      reportScanError('LICENSE:REQUEST_FAILED', describeThrownError(error));
      handleLicenseError();
      return false;
    }
  };

  // 連続スキャンループ
  const stopContinuousScan = () => {
    isContinuousScanning = false;
    isReading.value      = false;
    lastScanLoopEndedAt = 0;
    cancelScanSchedule();
    cancelScanIdleTimeout();
  };

  const runScanIteration = async() => {
    if (!isContinuousScanning || !cameraPreviewStarted || isPausedByAppBackground || isScanServiceDisposed) return;

    if (document.querySelector('.modal.show')) {
      scheduleNextScan(100);
      return;
    }

    if (isDecoding.value) {
      scheduleNextScan(100);
      return;
    }

    isDecoding.value = true;
    let stage = 'INIT';
    const waitMs = lastScanLoopEndedAt > 0
      ? roundMs(performance.now() - lastScanLoopEndedAt)
      : 0;
    const loopStartedAt = performance.now();
    const timingSeq = ++scanTimingSeq;
    let captureMs = null;
    let processMs = null;
    let bridgeMs = null;
    let decodeMs = null;
    let found = false;
    let decodedCodemark = null;
    let decodedCodedata = null;
    let frameWidth = null;
    let frameHeight = null;
    let source = null;
    let fallbackReason = null;

    try {

      if (!isLicenseReady.value) {
        stage = 'FETCH_LICENSE';
        const loaded = await fetchLicense();
        if (!loaded) {
          stopContinuousScan();
          return;
        }
      }

      stage = 'GET_CANVAS_SIZE';
      const { width, height } = getDecodeCanvasSize();
      frameWidth = width;
      frameHeight = height;

      if (!nativeDecodeAvailableForSession) {
        handleScanLoopError('NATIVE_UNAVAILABLE');
        return;
      }

      // ネイティブSDKで画像取得からバーコードのデコードまでを一括で行う（Android / iOS）
      stage = 'CAPTURE_AND_DECODE';
      const captureStartedAt = performance.now();
      const result = await captureAndDecode({ width, height });
      bridgeMs = roundMs(performance.now() - captureStartedAt);
      captureMs = toTimingMs(result?.captureMs);
      processMs = toTimingMs(result?.processMs);
      decodeMs = toTimingMs(result?.decodeMs);
      frameWidth = result?.width ?? width;
      frameHeight = result?.height ?? height;
      found = !!result?.found;
      source = result?.source ?? null;
      fallbackReason = result?.analysisFallbackReason ?? null;
      if (found) {
        decodedCodemark = result.codemark;
        decodedCodedata = result.codedata;
      }
    } catch (error) {
      if (!isContinuousScanning || isPausedByAppBackground || isScanServiceDisposed) {
        return;
      }
      if (isCameraSessionUnavailableError(error)) {
        stopContinuousScan();
        if (!isPausedByAppBackground && !isScanServiceDisposed) {
          await startCameraPreview();
        }
        return;
      }
      const oomTag = isLikelyOutOfMemoryError(error) ? '[OOM]' : '';
      handleScanLoopError(
        `${stage}${oomTag}`,
        describeThrownError(error),
      );
    } finally {
      const totalMs = roundMs(performance.now() - loopStartedAt);
      logScanTiming({
        n: timingSeq,
        captureMs,
        processMs,
        bridgeMs,
        decodeMs,
        waitMs,
        totalMs,
        found,
        width: frameWidth,
        height: frameHeight,
        source,
        fallbackReason,
        stage,
      });
      lastScanLoopEndedAt = performance.now();
      isDecoding.value = false;
    }

    if (found) {
      try {
        await handleDecodedResult(decodedCodemark, decodedCodedata);
      } catch (error) {
        handleDecodeError('HANDLE_DECODED_RESULT', describeThrownError(error));
      }
      return;
    }

    if (isContinuousScanning) {
      scheduleNextScan();
    }
  };

  const startContinuousScan = () => {
    if (isContinuousScanning) return;
    isContinuousScanning = true;
    isReading.value      = true;
    scanTimingSeq = 0;
    lastScanLoopEndedAt = 0;
    nativeDecodeAvailableForSession = isNativeDecodeAvailable();
    startScanIdleTimeout();
    runScanIteration();
  };

  // 「読み取る」ボタン押下エントリーポイント
  const startScan = async() => {
    if (!isCameraPreviewActive.value || isPausedByAppBackground || isScanServiceDisposed) return;
    if (isReading.value) return;
    prepareScanSuccessSound();
    startContinuousScan();
  }

  // CameraPreview位置計算
  const getAspectRatioCenterOffset = () => {
    const previewH = window.screen.width * 9 / 16;
    return Math.round((window.screen.height - previewH) / 2);
  }

  const getNativePreviewRect = () => {
    const areaEl = cameraAreaRef.value;
    if (!areaEl) return null;
    const rect     = areaEl.getBoundingClientRect();
    const viewport = window.visualViewport;
    return {
        x: 0,
        y: Math.round(rect.top + (viewport?.offsetTop ?? 0)),
        width: Math.round(window.innerWidth),
        height: Math.round(rect.height),
        rawTop: Math.round(rect.top),
        visualViewportOffsetTop: Math.round(viewport?.offsetTop ?? 0),
    };
  };

  /**
   * ステータスバー高さ（論理px）。edge-to-edge 端末での y 補正に使用。
   * Android: dp、iOS: points
   */
  const getWebViewTopInset = async () => {
    const platform = Capacitor.getPlatform();
    if (platform !== 'android' && platform !== 'ios') return 0;
    if (!(CameraPreview && typeof CameraPreview.getSafeAreaInsets === 'function')) return 0;

    try {
      const { top } = await CameraPreview.getSafeAreaInsets();
      return Math.round(top || 0);
    } catch {
      return 0;
    }
  };

  /** CameraPreview と同じ edge-to-edge 判定（Android のみ有効） */
  const getIsEdgeToEdgeActive = async () => {
    if (Capacitor.getPlatform() !== 'android') return false;
    if (!(WebViewLayout && typeof WebViewLayout.getIsEdgeToEdgeActive === 'function')) return false;

    try {
      const { active } = await WebViewLayout.getIsEdgeToEdgeActive();
      return !!active;
    } catch {
      return false;
    }
  };

  /** 元の算出方法で CameraPreview.start 用オプションを組み立てる */
  const buildCameraPreviewStartOptions = async (rect) => {
    const aspectRatioCenterOffset = getAspectRatioCenterOffset();


    const isIos = Capacitor.getPlatform() === 'ios';

    let options = {
      position: 'rear',
      toBack: true,
      disableAudio: true,
      lockAndroidOrientation: true,
      rotateWhenOrientationChanged: false,
      enableOpacity: true,
      initialZoomLevel: INITIAL_PREVIEW_ZOOM_LEVEL,
      x: rect.x,
      paddingBottom: 0,
    };

    if (isIos) {
      options.y = rect.y - aspectRatioCenterOffset -5;
      options.aspectRatio = '9:16';
    } else {
      // バッファーを設けて、Android の 位置ずれを抑える
      const buffer = 3;
      const androidTopInset = await getWebViewTopInset();
      const isEdgeToEdgeActive = await getIsEdgeToEdgeActive();
      options.y = isEdgeToEdgeActive
        ? Math.max(0, rect.y - buffer - androidTopInset)
        : Math.max(0, rect.y - buffer);
      options.width = rect.width;
      options.height = rect.height + (buffer * 2);
    }

    return options;
  };

  const captureFixedCameraPreviewStartOptions = async () => {
    const rect = getNativePreviewRect();
    if (!rect) return null;
    fixedCameraPreviewStartOptions = await buildCameraPreviewStartOptions(rect);
    return fixedCameraPreviewStartOptions;
  };

  // 向き変更リスナー
  const clearRestoreBoundsTimers = () => {
    restoreBoundsTimers.forEach((timerId) => clearTimeout(timerId));
    restoreBoundsTimers = [];
  };

  const clearFixedCameraPreviewStartOptions = () => {
    fixedCameraPreviewStartOptions = null;
    clearRestoreBoundsTimers();
  };

  /** 固定した start オプションでプレビュー位置を復元（setPreviewSize は使わない） */
  const restoreFixedCameraPreviewPosition = async () => {
    if (!cameraPreviewStarted || !fixedCameraPreviewStartOptions || isRestoringPreviewBounds) return;
    if (!(CameraPreview && typeof CameraPreview.start === 'function')) return;

    isRestoringPreviewBounds = true;
    try {
      await startCameraPreviewSession(fixedCameraPreviewStartOptions);
    } catch { /* do nothing */ } finally {
      isRestoringPreviewBounds = false;
    }
  };

  const scheduleRestoreFixedCameraPreviewPosition = (delays) => {
    clearRestoreBoundsTimers();
    delays.forEach((delay) => {
      const timerId = setTimeout(() => {
        restoreFixedCameraPreviewPosition();
      }, delay);
      restoreBoundsTimers.push(timerId);
    });
  };

  const stopRotationWatcher = () => {
    if (rotationWatcherCleanup) {
      rotationWatcherCleanup();
      rotationWatcherCleanup = null;
    }
  };

  /**
   * CameraPreview.orientationChange 時に固定座標でプレビュー位置を復元する。
   * @returns {Promise<void>}
  */
  const startOrientationRestoreListener = async() => {
    stopRotationWatcher();

    if (!(CameraPreview && typeof CameraPreview.addListener === 'function')) {
      return;
    }

    // スマホの向きが変わった時にプレビュー位置を復元する
    rotationPluginListenerHandle = await CameraPreview.addListener('orientationChange', () => {
        scheduleRestoreFixedCameraPreviewPosition(RESTORE_BOUNDS_DELAYS_ON_ORIENTATION_MS);
    });

    rotationWatcherCleanup = () => {
      if (rotationPluginListenerHandle) {
        rotationPluginListenerHandle.remove();
        rotationPluginListenerHandle = null;
      }
    };
  };

  const getCameraPreviewErrorMessage = (error) => {
    if (!error) return '';
    if (typeof error === 'string') return error;
    return error.message || error.errorMessage || '';
  };

  const ensureCameraPermission = async () => {
    if (!(CameraPreview && typeof CameraPreview.requestPermissions === 'function')) {
      return true;
    }

    let status = await CameraPreview.checkPermissions({ disableAudio: true });
    if (status?.camera !== 'granted') {
      status = await CameraPreview.requestPermissions({ disableAudio: true });
    }
    return status?.camera === 'granted';
  };

  /**
   * CameraPreview.start を実行する。
   * エミュレータ等で initialZoomLevel 1.2 が未対応の場合は 1.0 で再試行する。
   */
  const startCameraPreviewSession = async (options) => {
    const startOptions = { ...options, force: true };

    try {
      await CameraPreview.start(startOptions);
      return;
    } catch (error) {
      const message = getCameraPreviewErrorMessage(error);
      const zoomLevel = startOptions.initialZoomLevel;
      const isZoomError = /zoom/i.test(message);
      if (isZoomError && zoomLevel != null && zoomLevel !== 1) {
        await CameraPreview.start({ ...startOptions, initialZoomLevel: 1 });
        return;
      }
      throw error;
    }
  };

  // カメラ起動 / 停止
  const ensureCameraStopped = async() => {
    stopRotationWatcher();

    if (!(CameraPreview && typeof CameraPreview.stop === 'function')) {
      cameraPreviewStarted = false;
      isCameraPreviewActive.value = false;
      stopContinuousScan();
      document.body.classList.remove(cameraPreviewBodyClass);
      return;
    }

    try {
      // isRunning の結果に関わらず stop を試行（前画面の残骸を解放）
      await CameraPreview.stop({ force: true });
    } catch (error) {
      // do nothing
    } finally {
      cameraPreviewStarted = false;
      isCameraPreviewActive.value = false;
      stopContinuousScan();
      document.body.classList.remove(cameraPreviewBodyClass);
    }
  };

  const isNativePlatform = () => (
    typeof Capacitor.isNativePlatform === 'function'
      ? Capacitor.isNativePlatform()
      : (Capacitor.getPlatform && Capacitor.getPlatform() !== 'web')
  );

  /**
   * バックグラウンド遷移時は連続スキャンとネイティブカメラを止める。
   * 復帰時にプレビューを再作成する前提で、連続スキャンは自動再開しない。
   * pause / appStateChange の両方が来ても冪等に扱う。
   */
  const handleAppBackground = async () => {
    if (isScanServiceDisposed) return;
    if (cameraPreviewStarted || isContinuousScanning) {
      isPausedByAppBackground = true;
    }
    stopContinuousScan();
    if (cameraPreviewStarted) {
      await ensureCameraStopped();
    }
  };

  const handleAppForeground = async () => {
    if (isScanServiceDisposed || !isPausedByAppBackground) return;
    isPausedByAppBackground = false;
    await startCameraPreview();
  };

  const enqueueAppStateChange = (isActive) => {
    appStateChangeQueue = appStateChangeQueue
      .then(async () => {
        if (isActive) {
          await handleAppForeground();
        } else {
          await handleAppBackground();
        }
      })
      .catch(() => {});
  };

  /**
   * Android は appStateChange が Activity.onStop まで遅れる一方、
   * カメラは onPause で閉じる。pause / visibilitychange で先にスキャンを止める。
   */
  const onDocumentVisibilityChange = () => {
    if (isScanServiceDisposed) return;
    if (document.hidden) {
      if (cameraPreviewStarted || isContinuousScanning) {
        isPausedByAppBackground = true;
      }
      stopContinuousScan();
      enqueueAppStateChange(false);
      return;
    }
    enqueueAppStateChange(true);
  };

  const bindAppStateListener = async () => {
    if (isScanServiceDisposed || appStateListenerHandles.length > 0) return;
    if (!isNativePlatform()) return;
    if (!(App && typeof App.addListener === 'function')) return;

    document.addEventListener('visibilitychange', onDocumentVisibilityChange);

    const addLifecycleListener = async (eventName, listener) => {
      try {
        return await App.addListener(eventName, listener);
      } catch {
        return null;
      }
    };

    const handles = [
      await addLifecycleListener('appStateChange', ({ isActive }) => {
        enqueueAppStateChange(!!isActive);
      }),
      await addLifecycleListener('pause', () => {
        if (cameraPreviewStarted || isContinuousScanning) {
          isPausedByAppBackground = true;
        }
        stopContinuousScan();
        enqueueAppStateChange(false);
      }),
      await addLifecycleListener('resume', () => {
        enqueueAppStateChange(true);
      }),
    ].filter(Boolean);

    if (isScanServiceDisposed) {
      document.removeEventListener('visibilitychange', onDocumentVisibilityChange);
      await Promise.all(handles.map(async (handle) => {
        try {
          await handle.remove();
        } catch {
          // do nothing
        }
      }));
      return;
    }
    appStateListenerHandles = handles;
  };

  const unbindAppStateListener = async () => {
    document.removeEventListener('visibilitychange', onDocumentVisibilityChange);
    const handles = appStateListenerHandles;
    appStateListenerHandles = [];
    await Promise.all(handles.map(async (handle) => {
      try {
        await handle.remove();
      } catch {
        // do nothing
      }
    }));
  };

  const startCameraPreview = async() => {
    if (isScanServiceDisposed) return;
    scanError.value = '';
    await bindAppStateListener();
    await nextTick();
    if (isScanServiceDisposed) return;

    if (!fixedCameraPreviewStartOptions) {
      await captureFixedCameraPreviewStartOptions();
    }

    const cameraPreviewOptions = fixedCameraPreviewStartOptions;
    if (!cameraPreviewOptions) {
      setScanError('カメラ表示領域が見つかりません。', 'CAMERA_PREVIEW:AREA_NOT_FOUND');
      return;
    }

    // ネイティブアプリでは CameraPreview を優先
    if (isNativePlatform() && CameraPreview && typeof CameraPreview.start === 'function') {
      try {
        await ensureCameraStopped();

        const hasPermission = await ensureCameraPermission();
        if (!hasPermission) {
          setScanError(
            'カメラの使用が許可されていません。設定からカメラを有効にしてください。',
            'CAMERA_PREVIEW:PERMISSION_DENIED',
          );
          return;
        }

        await startCameraPreviewSession(cameraPreviewOptions);
        if (isScanServiceDisposed) {
          await ensureCameraStopped();
          return;
        }
        cameraPreviewStarted = true;
        isCameraPreviewActive.value = true;
        document.body.classList.add(cameraPreviewBodyClass);
        await startOrientationRestoreListener();
        return;
      } catch (error) {
        const detail = getCameraPreviewErrorMessage(error);
        setScanError(
          detail
            ? `カメラプレビューの起動に失敗しました。（${detail}）`
            : 'カメラプレビューの起動に失敗しました。',
          'CAMERA_PREVIEW:START_FAILED',
          describeThrownError(error),
        );
        return;
      }
    }
    setScanError('camera-preview が利用できない環境です。', 'CAMERA_PREVIEW:UNAVAILABLE');
  };

  const stopCameraPreview = async() => {
    isScanServiceDisposed = true;
    isPausedByAppBackground = false;
    await unbindAppStateListener();
    stopContinuousScan();
    isScanSuccessHighlight.value = false;
    await ensureCameraStopped();
    clearFixedCameraPreviewStartOptions();
  };

  return {
    // テンプレートバインド用 refs
    cameraAreaRef,
    videoRef,
    canvasRef,
    scanError,
    isCameraPreviewActive,
    isReading,
    isScanSuccessHighlight,
    errorMessages,
    // ライフサイクルフック用
    fetchLicense,
    startCameraPreview,
    stopCameraPreview,
    stopRotationWatcher,
    // ボタン操作用
    startScan,
  };
}